import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useState, useCallback, MouseEvent } from "react";
import * as Icon from "react-bootstrap-icons";
import { stringLimiter } from "./DraggableField.utils";

interface DraggableFieldProps {
  handleBtnClick?: (label: string) => void;
  label: string; // Field name (e.g., "Signature")
  value: string; // Marker base (e.g., "Signature")
  type: string;  // Field type (e.g., "Signature")
}

const DraggableField: React.FC<DraggableFieldProps> = ({
  handleBtnClick,
  label,
  value,
  type
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isRequired, setIsRequired] = useState(false);

  const isSupportedField = (() => {
    const normalizedType = type?.toLowerCase();
    const normalizedValue = value?.toLowerCase();

    return (
      (normalizedType === "signature" && normalizedValue === "signature") ||
      (normalizedType === "signature" && normalizedValue === "text") ||
      (normalizedType === "signature" && normalizedValue === "checkbox") ||
      (normalizedType === "signature" && normalizedValue === "radiobutton")
    );
  })();


  const getFieldPlaceholderText = (fieldType: string, name: string): string => {
    switch (fieldType) {
      case "Signature": {
        const recipient = localStorage.getItem("CurrentRecipientId") || "1";
        const requirement = isRequired ? "R" : "O"; // R=Required, O=Optional
        // «Signature:R:Signer1»
        if(isSupportedField)
          return `«${name}:${requirement}:Signer${recipient}»`;
        else
          return `«${name}:Signer${recipient}»`;
      }
      case "Selected Field":
        return `{{${name}}}`;
      case "Rule":
        return `{${name}}`;
      case "Content Block":
        return `[[${name}]]`;
      default:
        return `«${name}»`;
    }
  };

  const handleCopyClick = (text: string, event: MouseEvent<SVGElement>) => {
    event.stopPropagation();
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setShowTooltip(true);
        setTimeout(() => setShowTooltip(false), 2000);
      })
      .catch((err) => console.error("Failed to copy text: ", err));
  };

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLButtonElement>) => {
      const dragImage = document.createElement("div");
      dragImage.className = "dropped-field";
      dragImage.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
             fill="currentColor" class="bi bi-grip-vertical" viewBox="0 0 16 16"
             style="margin-left: 0.1rem;">
          <path d="M7 2a1 1 0 1 1-2 0 1 1 0 0 1 2 0m3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0M7 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0m3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0M7 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0m3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0m-3 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0m3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0m-3 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
        </svg>
        <span>${label}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
             fill="currentColor" class="bi bi-x-lg delete-button" viewBox="0 0 16 16">
          <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
        </svg>
      `;
      document.body.appendChild(dragImage);
      e.dataTransfer.setData(
        "text/plain",
        getFieldPlaceholderText(type, value) + "\n"
      );
      setTimeout(() => document.body.removeChild(dragImage), 0);
    },
    [label, type, value, isRequired]
  );

  const placeholder = getFieldPlaceholderText(type, value);

  return (
    <button
      type="button"
      draggable
      className="draggable-field"
      onDragStart={handleDragStart}
      onClick={() => handleBtnClick?.(label)}
    >
      <div className="icon-wrapper">
        <Icon.GripVertical style={{ marginRight: 6 }} />
      </div>

      <div style={{ display: "flex", flex: "1 1 auto" }}>
        {stringLimiter(label)}
      </div>

      {isSupportedField && (
        <OverlayTrigger
          placement="left"
          overlay={
            <Tooltip id={`req-tip-${label}`}>
              Mark as {isRequired ? "Optional (:O)" : "Required (:R)"}
            </Tooltip>
          }
        >
          <label
            className="icon-wrapper req-toggle"
            onClick={(e) => e.stopPropagation()}
            style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", marginRight: "5px" }}
          >
            <input
              type="checkbox"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
            />
          </label>
        </OverlayTrigger>
      )}

      <OverlayTrigger
        trigger="click"
        placement="top"
        show={showTooltip}
        overlay={
          <Tooltip style={{ zIndex: 10001 }} id={`tooltip-${label}`}>
            Copied
          </Tooltip>
        }
      >
        <div className="icon-wrapper">
          <Icon.ClipboardFill
            onClick={(event) => handleCopyClick(placeholder, event)}
            style={{ cursor: "pointer", color: "black", fontSize: 12 }}
          />
        </div>
      </OverlayTrigger>
    </button>
  );
};

export default DraggableField;
